import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Office = {
  id: string;
  name: string;
  region: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  map_embed_url: string | null;
};

const FALLBACK: Office[] = [
  { id: "f1", name: "Riyadh HQ", region: "Saudi Arabia", address: "Riyadh, Saudi Arabia", phone: "+966 55 975 8082", email: "hello@coderlity.com", map_embed_url: null },
];

export function LocationMap() {
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["public-branches"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("branches")
        .select("id, name, region, address, phone, email, map_embed_url")
        .eq("status", "active")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Office[];
    },
  });

  const offices = data && data.length > 0 ? data : FALLBACK;
  const active = offices.find((o) => o.id === activeId) ?? offices[0];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 rounded-2xl border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-6">
      <div className="flex flex-col gap-2">
        {offices.map((o) => {
          const isActive = o.id === active.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setActiveId(o.id)}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition ${
                isActive
                  ? "border-primary bg-gradient-brand/10 text-foreground shadow-card"
                  : "border-border bg-card hover:border-primary/40 hover:bg-muted/40"
              }`}
            >
              <span className="inline-flex h-8 w-10 items-center justify-center rounded-md bg-muted text-base">
                <MapPin className="h-4 w-4 text-primary" />
              </span>
              <span className="font-medium">{o.name}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <h3 className="text-base font-semibold text-primary">{active.name}</h3>
              {active.address && (
                <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{active.address}{active.region ? `, ${active.region}` : ""}</span>
                </p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-semibold">Phone Number</h4>
              {active.phone ? (
                <a
                  href={`tel:${active.phone.replace(/\s/g, "")}`}
                  className="mt-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                >
                  <Phone className="h-4 w-4 text-primary" />
                  {active.phone}
                </a>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">—</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-semibold">Email Address</h4>
              {active.email ? (
                <a
                  href={`mailto:${active.email}`}
                  className="mt-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary break-all"
                >
                  <Mail className="h-4 w-4 text-primary" />
                  {active.email}
                </a>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">—</p>
              )}
            </div>
          </div>
        </div>

        {active.map_embed_url ? (
          <div
            key={active.id}
            className="overflow-hidden rounded-2xl border border-border bg-card shadow-card animate-in slide-in-from-left-4 duration-300"
          >
            <iframe
              src={active.map_embed_url}
              title={`${active.name} map`}
              className="h-[360px] w-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <div className="flex h-[200px] items-center justify-center rounded-2xl border border-dashed border-border bg-muted/20 text-sm text-muted-foreground">
            No map available for this office
          </div>
        )}
      </div>
    </div>
  );
}
